/**
 * Customer Lookup Page
 *
 * Staff tool for looking up customer information, loans, and collateral.
 * Requires members:read permission.
 *
 * FI staff read 'customer' for the person and 'account number' for the
 * FI's own identifier (Borrower.externalId, carried here as memberNumber),
 * per the fi-person-noun-and-signature-label ruling. This is a person
 * surface; 'Borrower' stays only where a loan-context column names the loan
 * party (customer-noun-loan-columns (a)).
 *
 * The /admin/members endpoints have no canonical OpenAPI declaration yet, so
 * the two response views below are local. They name only the fields this page
 * renders. The customer record carries no stored status, and the endpoint's
 * activity list is always empty, so neither is rendered: a derived "ACTIVE"
 * pill and an empty Activity tab both read as facts the platform does not hold.
 */

import { useState } from 'react';
import {
  Search as SearchIcon,
  Landmark as LoanIcon,
  TrendingUp as CollateralIcon,
  Eye as ViewIcon,
  Phone as PhoneIcon,
  Mail as EmailIcon,
} from 'lucide-react';
import {
  Heading,
  BodyText,
  Card,
  Button,
  Input,
  DataTable,
  Alert,
  EmptyState,
  Progress,
  Badge,
  type DataTableColumn,
  TableScroll,
} from '@aaimplatform/ui';
import { useAuthStore } from '../stores/auth';
import { apiClient } from '../api/client';
import { readErrorCopy } from '../api/error-message';
import { loanStatusLabel, loanStatusTone, loanTypeLabel } from '../api/loan-labels';

interface MemberSearchResult {
  id: string;
  memberNumber: string;
  name: string;
  email?: string;
  phone?: string;
  activeLoans: number;
  /**
   * Null or absent when none of the customer's loans carries a valued
   * collateral package; an unvalued package is never summed as $0
   * (r3-fi-g8-9).
   */
  totalCollateralValue?: number | null;
}

interface MemberDetail {
  id: string;
  memberNumber: string;
  name: string;
  email?: string;
  phone?: string;
  loans: Array<{
    id: string;
    loanNumber: string;
    type: string;
    status: string;
    balance: number;
    /**
     * The loan's stored credit limit; absent when none is stored. It is never
     * the principal amount standing in for one (ruling 71).
     */
    limit?: number;
    /** Null when the loan has no collateral package or no computed ratio. */
    ltv: number | null;
  }>;
  collateral: {
    /** Null or absent when no package is valued; never a measured $0 (r3-fi-g8-9). */
    totalValue?: number | null;
    /** Null or absent when no package is valued; never a measured $0 (r3-fi-g8-9). */
    pledgedValue?: number | null;
    marginUtilization: number;
    accounts: Array<{
      institution: string;
      /** Null when the position carries no market value; never summed as $0. */
      value: number | null;
    }>;
  };
}

/** The permission the members endpoints are gated on, lowercase per authz-case-insensitive.md. */
const MEMBERS_READ = 'members:read';

/** True when the grant list carries members:read in any case. */
function canReadMembers(permissions: readonly unknown[] | undefined | null): boolean {
  return (permissions ?? []).some(
    (p) => typeof p === 'string' && p.toLowerCase() === MEMBERS_READ,
  );
}

const SEARCH_FAILED_COPY = 'Customer search could not be completed. Retry, or contact support if it persists.';
const DETAIL_FAILED_COPY = 'Customer details could not be loaded. Retry, or contact support if it persists.';

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

/** The words a loan's credit limit prints when none is stored. */
const NOT_RECORDED = 'Not recorded';

/** The words a collateral total prints when no package behind it was valued. */
const NOT_VALUED = 'Not valued';

/** A collateral total, or the words for one no valuation produced. */
const formatCollateralValue = (amount: number | null | undefined) =>
  amount == null ? NOT_VALUED : formatCurrency(amount);

/** One decimal place: the ratio arrives as ratio * 100 and carries float noise (0.07 * 100). */
const formatPercent = (value: number) => `${Math.round(value * 10) / 10}%`;

export function MemberLookupPage() {
  const { user } = useAuthStore();
  const [searchQuery, setSearchQuery] = useState('');
  /** The query the shown results answer; null before the first search. */
  const [searchedQuery, setSearchedQuery] = useState<string | null>(null);
  const [searchResults, setSearchResults] = useState<MemberSearchResult[]>([]);
  const [selectedMember, setSelectedMember] = useState<MemberDetail | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canViewMembers = canReadMembers(user?.permissions);

  const handleSearch = async () => {
    const q = searchQuery.trim();
    if (!q) return;

    setLoading(true);
    setError(null);
    setSelectedMember(null);

    try {
      const data = await apiClient.get<MemberSearchResult[]>(
        `/admin/members/search?q=${encodeURIComponent(q)}`,
      );
      setSearchResults(data ?? []);
      setSearchedQuery(q);
    } catch (err) {
      setError(readErrorCopy(err, SEARCH_FAILED_COPY));
      setSearchResults([]);
      setSearchedQuery(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectMember = async (memberId: string) => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiClient.get<MemberDetail>(`/admin/members/${memberId}`);
      setSelectedMember(data);
      setActiveTab(0);
    } catch (err) {
      setError(readErrorCopy(err, DETAIL_FAILED_COPY));
      setSelectedMember(null);
    } finally {
      setLoading(false);
    }
  };

  if (!canViewMembers) {
    return (
      <div className="max-w-3xl mx-auto mt-8">
        <Alert type="warning">You do not have permission to view customer information.</Alert>
      </div>
    );
  }

  // DataTable prints String(accessor(row)) unless a column has `cell`, so an
  // accessor returning JSX rendered "[object Object]" in every cell and the
  // View control never existed. Markup goes in `cell`.
  const searchResultColumns: DataTableColumn<MemberSearchResult>[] = [
    {
      header: 'Customer',
      accessor: 'name',
      cell: (_value, member) => (
        <div>
          <div className="font-medium text-primary">{member.name}</div>
          <div className="text-sm text-tertiary">{member.memberNumber}</div>
        </div>
      ),
    },
    {
      header: 'Loans',
      accessor: 'activeLoans',
      monospace: true,
      align: 'right',
    },
    {
      header: 'Collateral',
      accessor: 'totalCollateralValue',
      cell: (_value, member) => formatCollateralValue(member.totalCollateralValue),
      monospace: true,
      align: 'right',
    },
    {
      header: '',
      accessor: 'id',
      cell: (_value, member) => (
        <button
          type="button"
          onClick={() => handleSelectMember(member.id)}
          className="p-2 text-secondary hover:text-primary"
          aria-label={`View ${member.name}`}
        >
          <ViewIcon aria-hidden="true" />
        </button>
      ),
    },
  ];

  const collateral = selectedMember?.collateral;

  return (
    <div>
      {/* Page Heading */}
      <div className="mb-6">
        {/* Page title lives in the sticky topnav (AppHeader); the body opens
            with the descriptive subtitle, no redundant H1. */}
        <p className="text-sm font-sans text-tertiary">Search and view customer account details</p>
      </div>

      {/* Search */}
        <Card className="mb-6">
          {/* Stacks below 640px: one row squeezed the input beside the Search
              button until its placeholder was cut mid-word (fi-g8-21). */}
          <div className="flex flex-col gap-3 sm:flex-row sm:gap-4">
            <div className="flex-1 relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-tertiary">
                <SearchIcon />
              </div>
              {/* Lists what /admin/members/search matches: every term must hit
                  first name, last name, email, phone or account number
                  (externalId), so a full name narrows (fi-g8-12). */}
              <Input
                className="pl-10"
                placeholder="Name, email, phone, or account number"
                aria-label="Search customers"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
            <Button variant="primary" onClick={handleSearch} disabled={loading}>
              Search
            </Button>
          </div>
        </Card>

        {loading && <Progress label="Loading" size="sm" className="mb-4" />}

        {error && (
          <Alert type="error" className="mb-4">{error}</Alert>
        )}

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {/* Search Results */}
          <div className={selectedMember ? 'md:col-span-5' : 'md:col-span-12'}>
            {searchResults.length > 0 ? (
              <DataTable
                columns={searchResultColumns}
                data={searchResults}
                striped
                hoverable
              />
            ) : searchedQuery !== null && !loading ? (
              <EmptyState
                title={`No customers match "${searchedQuery}"`}
                description="Search matches a full or partial name, email, phone, or account number. Every word must match."
              />
            ) : null}
          </div>

          {/* Customer Detail */}
          {selectedMember && collateral && (
            <div className="md:col-span-7">
              <Card>
                {/* Header */}
                <div className="mb-6">
                  <Heading level={2} size="section">{selectedMember.name}</Heading>
                  <BodyText className="text-secondary">{selectedMember.memberNumber}</BodyText>
                </div>

                {/* Contact Info */}
                <div className="flex flex-wrap gap-6 mb-6">
                  {selectedMember.email && (
                    <div className="flex items-center gap-2">
                      <EmailIcon size={20} className="text-tertiary" aria-hidden="true" />
                      <BodyText className="text-sm">{selectedMember.email}</BodyText>
                    </div>
                  )}
                  {selectedMember.phone && (
                    <div className="flex items-center gap-2">
                      <PhoneIcon size={20} className="text-tertiary" aria-hidden="true" />
                      <BodyText className="text-sm">{selectedMember.phone}</BodyText>
                    </div>
                  )}
                </div>

                {/* Tab Navigation */}
                <div className="flex border-b border-recessed mb-4">
                  <button
                    type="button"
                    onClick={() => setActiveTab(0)}
                    className={`px-4 py-2 font-medium border-b-2 transition-colors ${activeTab === 0 ? 'border-primary text-primary' : 'border-transparent text-secondary hover:text-primary'}`}
                  >
                    <LoanIcon size={20} className="inline mr-2" aria-hidden="true" />
                    Loans
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab(1)}
                    className={`px-4 py-2 font-medium border-b-2 transition-colors ${activeTab === 1 ? 'border-primary text-primary' : 'border-transparent text-secondary hover:text-primary'}`}
                  >
                    <CollateralIcon size={20} className="inline mr-2" aria-hidden="true" />
                    Collateral
                  </button>
                </div>

                {activeTab === 0 && (
                  selectedMember.loans.length === 0 ? (
                    <BodyText className="text-sm text-secondary">No loans on record for this customer.</BodyText>
                  ) : (
                  <TableScroll label="Customer loans">
                    <table className="w-full min-w-table-dense">
                      <thead className="border-b border-recessed">
                        <tr>
                          <th className="text-left py-2 px-3 text-sm font-medium text-secondary">Loan</th>
                          <th className="text-left py-2 px-3 text-sm font-medium text-secondary">Type</th>
                          <th className="text-left py-2 px-3 text-sm font-medium text-secondary">Status</th>
                          <th className="text-right py-2 px-3 text-sm font-medium text-secondary">Balance</th>
                          <th className="text-right py-2 px-3 text-sm font-medium text-secondary">Credit Limit</th>
                          <th className="text-right py-2 px-3 text-sm font-medium text-secondary">LTV</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedMember.loans.map((loan) => (
                          <tr key={loan.id} className="border-b border-recessed last:border-0">
                            <td className="py-2 px-3 text-sm font-mono">{loan.loanNumber}</td>
                            <td className="py-2 px-3 text-sm">{loanTypeLabel(loan.type)}</td>
                            <td className="py-2 px-3">
                              <Badge variant={loanStatusTone(loan.status)} size="small">
                                {loanStatusLabel(loan.status)}
                              </Badge>
                            </td>
                            <td className="py-2 px-3 text-sm font-mono text-right">{formatCurrency(loan.balance)}</td>
                            {/* The stored credit limit, or 'Not recorded'. A loan
                                with none stored reported its principal as one
                                (ruling 71). */}
                            <td className="py-2 px-3 text-sm font-mono text-right">
                              {loan.limit == null ? NOT_RECORDED : formatCurrency(loan.limit)}
                            </td>
                            <td className="py-2 px-3 text-sm font-mono text-right">
                              {loan.ltv == null ? '--' : formatPercent(loan.ltv)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </TableScroll>
                  )
                )}

                {activeTab === 1 && (
                  <div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                      <div className="border border-recessed rounded p-4 text-center bg-surface">
                        <div className="text-xs text-secondary mb-1">Market Value</div>
                        <div className="text-lg font-semibold font-mono">{formatCollateralValue(collateral.totalValue)}</div>
                      </div>
                      <div className="border border-recessed rounded p-4 text-center bg-surface">
                        <div className="text-xs text-secondary mb-1">Eligible Value</div>
                        <div className="text-lg font-semibold font-mono">{formatCollateralValue(collateral.pledgedValue)}</div>
                      </div>
                      <div className="border border-recessed rounded p-4 text-center bg-surface">
                        <div className="text-xs text-secondary mb-1">Eligible Share</div>
                        {/* A share of $0 market value is undefined, not 0%. */}
                        <div className="text-lg font-semibold font-mono" data-testid="eligible-share">
                          {collateral.totalValue != null &&
                          collateral.totalValue > 0 &&
                          collateral.marginUtilization != null
                            ? `${collateral.marginUtilization}%`
                            : '--'}
                        </div>
                      </div>
                    </div>
                    <div className="text-sm font-medium text-primary mb-2">Linked Accounts</div>
                    {collateral.accounts.length === 0 ? (
                      <BodyText className="text-sm text-secondary">No linked custody accounts.</BodyText>
                    ) : (
                      collateral.accounts.map((acc, i) => (
                        <div key={i} className="flex justify-between py-2 border-b border-recessed last:border-0">
                          <BodyText className="text-sm">{acc.institution}</BodyText>
                          <BodyText className="text-sm font-mono">
                            {acc.value == null ? 'Not priced' : formatCurrency(acc.value)}
                          </BodyText>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </Card>
            </div>
          )}
        </div>
    </div>
  );
}
